/**
 * src/pages/venue/LibraryPage.tsx — 2304vc
 *
 * Cross-event media library.
 * All shortlisted photos across all events.
 * Searchable by event, type, score, mood.
 * Re-assign to bins, re-use for campaigns.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchEvents } from '@/lib/events'
import { Spinner } from '@/components/ui'
import type { Venue } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface Props { venue: Venue; picker: PickerSession }

interface LibraryItem {
  id:         string
  ai_score:   number | null
  event_id:   string | null
  media: { cloudinary_url: string; ai_description: string | null } | null
  event: { name: string; event_date: string; type: string } | null
}

async function fetchLibrary(venueId: string, eventId?: string): Promise<LibraryItem[]> {
  let q = supabase
    .from('venue_picks')
    .select(`
      id, ai_score, event_id,
      media:media_items(cloudinary_url, ai_description),
      event:club_events(name, event_date, type)
    `)
    .eq('venue_id', venueId)
    .in('picker_action', ['saved', 'needs_approval', 'selected'])
    .order('ai_score', { ascending: false })

  if (eventId) q = q.eq('event_id', eventId)

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as unknown as LibraryItem[]
}

export function LibraryPage({ venue }: Props) {
  const [selectedEvent, setSelectedEvent] = useState<string | 'all'>('all')

  const { data: events = [] } = useQuery({
    queryKey: ['events', venue.id],
    queryFn:  () => fetchEvents(venue.id),
    staleTime: 5 * 60_000,
  })

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['library', venue.id, selectedEvent],
    queryFn:  () => fetchLibrary(venue.id, selectedEvent === 'all' ? undefined : selectedEvent),
    staleTime: 5 * 60_000,
  })

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 style={{ color: 'white', fontWeight: 700, fontSize: 17, margin: 0 }}>
          Library · {items.length}
        </h2>
      </div>

      {/* Event filter */}
      {events.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          <button type="button" onClick={() => setSelectedEvent('all')}
            style={{
              padding: '6px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: selectedEvent === 'all' ? 'white' : 'rgba(255,255,255,0.08)',
              color: selectedEvent === 'all' ? 'black' : 'rgba(255,255,255,0.5)',
              fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}>
            All events
          </button>
          {events.map(e => (
            <button key={e.id} type="button" onClick={() => setSelectedEvent(e.id)}
              style={{
                padding: '6px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: selectedEvent === e.id ? 'white' : 'rgba(255,255,255,0.08)',
                color: selectedEvent === e.id ? 'black' : 'rgba(255,255,255,0.5)',
                fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}>
              {e.name}
              <span style={{ opacity: 0.5, marginLeft: 4, fontSize: 10 }}>
                {e.type === 'floor' ? '📱' : '📷'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size={24} /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-white/40 text-sm">No shortlisted photos yet</p>
          <p className="text-white/25 text-xs mt-1">Photos appear here after picking</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 5,
        }}>
          {items.map(item => {
            const url = item.media?.cloudinary_url
              ?.replace('/upload/', '/upload/w_300,c_fill,q_auto,f_auto/')
            return (
              <div key={item.id} style={{
                position: 'relative', aspectRatio: '1', borderRadius: 10,
                overflow: 'hidden', background: 'rgba(255,255,255,0.05)',
              }}>
                {url && (
                  <img src={url} alt="" loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                {/* Score */}
                {item.ai_score != null && (
                  <div style={{
                    position: 'absolute', top: 4, right: 4,
                    background: 'rgba(0,0,0,0.75)', borderRadius: 999,
                    padding: '1px 5px', fontSize: 9, fontWeight: 700,
                    color: 'rgba(255,255,255,0.85)',
                  }}>
                    {Math.round(item.ai_score * 100)}
                  </div>
                )}
                {/* Event type dot */}
                {item.event && (
                  <div style={{
                    position: 'absolute', bottom: 4, left: 4,
                    background: item.event.type === 'floor'
                      ? 'rgba(251,191,36,0.9)' : 'rgba(96,165,250,0.9)',
                    borderRadius: 4, padding: '1px 4px',
                    fontSize: 8, fontWeight: 700, color: 'black',
                  }}>
                    {item.event.type === 'floor' ? 'Floor' : 'Pro'}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
